import axios from 'axios';
import { HOST_API } from '../config';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: HOST_API,
});

// Track if we're currently refreshing to prevent multiple refresh calls
let isRefreshing = false;
let failedQueue = [];
let lastRefreshTime = 0;
const REFRESH_COOLDOWN = 2000; // 2 seconds cooldown between refreshes

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Local copy of jwt.js's notifyFlutterLogout (jwt.js imports this module, so
// importing it back would create a require cycle). Tells the Flutter shell to
// clear its token copy and unregister the FCM device.
const notifyFlutterLogout = () => {
  try {
    if (window.Flutter) {
      window.Flutter.postMessage(JSON.stringify({ type: 'logout' }));
      console.log('📤 Notified Flutter about logout');
    }
  } catch (e) {
    // Not in the WebView — nothing to do
  }
};

// Request interceptor to add campus_id
axiosInstance.interceptors.request.use(
  (config) => {
    // Don't add campus_id to token refresh requests
    if (config.url?.includes('/refresh_token/')) {
      return config;
    }

    // Add access token to headers
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Get campus from localStorage as a fallback in case Redux isn't available
    let activeCampus = null;
    try {
      const savedCampus = localStorage.getItem('activeCampus');
      if (savedCampus) {
        activeCampus = JSON.parse(savedCampus);
      }
    } catch (error) {
      console.error('Error parsing activeCampus from localStorage:', error);
    }

    // Add campus_id to query params if available, but don't override an
    // explicit campus_id already set by the caller (e.g. in-page campus switcher)
    if (activeCampus?.id && !config.params?.campus_id) {
      config.params = {
        ...config.params,
        campus_id: activeCampus.id,
      };
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh on 401
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // A cancelled request is not a failure. It is a page replacing its own
    // in-flight query, and it has to leave here as the CanceledError axios
    // raised: the flattening at the bottom of this handler turns everything
    // without a response body into the bare string 'Something went wrong', and
    // a string carries no name and no code, so no caller can tell an abort from
    // a real error. Pages that check with axios.isCancel cannot catch it either,
    // because axios attaches that helper to its default export and never to an
    // instance from axios.create(). Hence the guard here, at the one point every
    // request in the app passes through.
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    // Skip refresh logic for login/auth endpoints — let them propagate naturally
    const isAuthEndpoint = originalRequest?.url?.includes('/login') || originalRequest?.url?.includes('/token/');
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      // Check if Flutter just refreshed the token (within last 2 seconds)
      const currentTime = Date.now();
      const timeSinceLastRefresh = currentTime - lastRefreshTime;

      // Get current tokens before checking anything
      const currentAccessToken = localStorage.getItem('accessToken');
      const currentRefreshToken = localStorage.getItem('refreshToken');

      // If tokens were just updated (likely by Flutter), retry with new token immediately
      if (timeSinceLastRefresh < REFRESH_COOLDOWN && currentAccessToken) {
        console.log('🔄 Using recently refreshed token from Flutter');
        originalRequest.headers.Authorization = `Bearer ${currentAccessToken}`;
        lastRefreshTime = currentTime;
        return axiosInstance(originalRequest);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        console.log('⏳ Token refresh in progress, queuing request...');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      // Add small delay to allow Flutter to handle refresh first
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Double-check if Flutter updated tokens during the delay
      const latestAccessToken = localStorage.getItem('accessToken');
      if (latestAccessToken && latestAccessToken !== currentAccessToken) {
        console.log('✅ Flutter already refreshed token, using new token');
        isRefreshing = false;
        lastRefreshTime = Date.now();
        processQueue(null, latestAccessToken);
        originalRequest.headers.Authorization = `Bearer ${latestAccessToken}`;
        return axiosInstance(originalRequest);
      }

      // If Flutter signalled it is mid-refresh (flutterTokenRefreshingAt, set
      // by the app's ApiClient), wait for it to finish instead of racing it —
      // with refresh-token rotation the loser of the race gets blacklisted.
      const flutterFlag = parseInt(localStorage.getItem('flutterTokenRefreshingAt') || '0', 10);
      if (flutterFlag && Date.now() - flutterFlag < 15000) {
        console.log('⏳ Flutter is refreshing the token, waiting for it...');
        for (let i = 0; i < 16; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 500));
          const tokenNow = localStorage.getItem('accessToken');
          if (tokenNow && tokenNow !== currentAccessToken) {
            console.log('✅ Flutter finished refreshing, using its token');
            isRefreshing = false;
            lastRefreshTime = Date.now();
            processQueue(null, tokenNow);
            originalRequest.headers.Authorization = `Bearer ${tokenNow}`;
            return axiosInstance(originalRequest);
          }
          // Flutter cleared its flag without a new token — its refresh failed
          // (or was transient); fall through to our own attempt.
          if (!localStorage.getItem('flutterTokenRefreshingAt')) break;
        }
      }

      // Proceed with React's refresh logic
      if (!currentRefreshToken) {
        console.log('❌ No refresh token available');
        isRefreshing = false;
        processQueue(new Error('No refresh token'), null);
        notifyFlutterLogout();
        localStorage.clear();
        window.location.href = '/user/login';
        return Promise.reject(error);
      }

      try {
        console.log('🔄 Refreshing access token...');

        // Signal to Flutter that React is handling the refresh.
        // Flutter polls this flag and waits instead of firing its own refresh
        // request — prevents both sides hitting the backend with the same
        // refresh token simultaneously (rotation race → blacklisted token → logout).
        localStorage.setItem('tokenRefreshingAt', Date.now().toString());

        // Use fetch instead of axiosInstance to avoid interceptor recursion
        const response = await fetch(`${HOST_API}/api/v1/accounts/refresh_token/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh: currentRefreshToken }),
        });

        if (!response.ok) {
          const refreshFailure = new Error(`Refresh failed with status ${response.status}`);
          refreshFailure.status = response.status;
          throw refreshFailure;
        }

        const data = await response.json();
        const { access, refresh: newRefreshToken } = data;

        // Store new tokens
        localStorage.setItem('accessToken', access);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        // Clear the coordination flag — Flutter can now read the new tokens
        localStorage.removeItem('tokenRefreshingAt');

        // Update timestamp
        lastRefreshTime = Date.now();

        // Update auth header
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${access}`;
        originalRequest.headers.Authorization = `Bearer ${access}`;

        console.log('✅ Token refreshed successfully by React');

        // Notify Flutter about token update (webview_flutter exposes channels as window.<channelName>)
        if (window.onTokensUpdated) {
          try {
            window.onTokensUpdated.postMessage(
              JSON.stringify({
                accessToken: access,
                refreshToken: newRefreshToken || currentRefreshToken,
              })
            );
            console.log('📤 Notified Flutter about token update');
          } catch (e) {
            console.log('Note: Flutter notification skipped (not in WebView)');
          }
        }

        processQueue(null, access);
        isRefreshing = false;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
      
        const latestAccessToken = localStorage.getItem('accessToken');
        if (latestAccessToken && latestAccessToken !== currentAccessToken) {
          console.log('✅ Flutter refreshed token during React attempt — retrying with new token');
          isRefreshing = false;
          localStorage.removeItem('tokenRefreshingAt');
          processQueue(null, latestAccessToken);
          originalRequest.headers.Authorization = `Bearer ${latestAccessToken}`;
          return axiosInstance(originalRequest);
        }

        processQueue(refreshError, null);
        isRefreshing = false;

        // Clear coordination flag so Flutter doesn't wait indefinitely
        localStorage.removeItem('tokenRefreshingAt');

        // Only a definitive rejection of the refresh token (400/401/403)
        // means the session is dead. Network errors and 5xx responses
        // (flaky mobile connection, backend deploy) are transient — keep the
        // tokens and let a later request retry instead of logging out.
        const refreshStatus = refreshError?.status;
        const isDefinitive =
          refreshStatus === 400 || refreshStatus === 401 || refreshStatus === 403;
        if (!isDefinitive) {
          console.warn('⚠️ Transient token refresh failure, keeping session:', refreshError);
          return Promise.reject(refreshError);
        }

        // Refresh token rejected — logout
        console.error('❌ Refresh token rejected, logging out');
        notifyFlutterLogout();
        localStorage.clear();
        window.location.href = '/user/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject((error.response && error.response.data) || 'Something went wrong');
  }
);

export default axiosInstance;
